/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2023 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2026 (c) Nico Brandt, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import { SoftwareAction } from './SoftwareActions';
import { createReducer } from '@reduxjs/toolkit';
import { Software } from 'Api/generated';

export interface ISoftwaresState {
  softwaresRequested: boolean;
  softwares: {
    // Key is Software.SoftwareId + Software.Id
    [key: string]: Software;
  };
  softwareIds: Software['SoftwareId'][];
  requestedToDelete: number[];
  deleted: Software[];
}
const initialState: ISoftwaresState = {
  softwaresRequested: false,
  softwares: {},
  softwareIds: [],
  requestedToDelete: [],
  deleted: [],
};

const SoftwareReducer = createReducer<ISoftwaresState>(initialState, (builder) => {
  builder.addCase(SoftwareAction.Delete, (state, action) => {
    const requestedToDelete = [...state.requestedToDelete, action.payload];
    return {
      ...state,
      requestedToDelete,
    };
  });

  builder.addCase(SoftwareAction.Deleted, (state: ISoftwaresState, action) => {
    const deleted = [...state.deleted, action.payload];
    const softwares = { ...state.softwares };
    if (action.payload.Id) {
      delete softwares[action.payload.Id];
    }
    const ids = new Set(state.softwareIds);
    ids.delete(action.payload.Data);

    return {
      ...state,
      softwares: { ...softwares },
      deleted,
      softwareIds: Array.from(ids),
    };
  });

  builder.addCase(SoftwareAction.Query, (state) => {
    return {
      ...state,
      softwaresRequested: true,
    };
  });

  const arrayToObject = (arr: Software[], key1: keyof Software, key2: keyof Software) => {
    return Object.assign({}, ...arr.map((item) => ({ [item[key1] + item[key2]]: item })));
  };

  builder.addCase(SoftwareAction.Queried, (state, action) => {
    const softwareIds = action.payload.map((software) => software.SoftwareId);
    return {
      ...state,
      softwaresRequested: false,
      softwares: arrayToObject(action.payload, 'SoftwareId', 'Id'),
      softwareIds,
    };
  });

  builder.addCase(SoftwareAction.Updated, (state, action) => {
    const softwareIds = [...state.softwareIds];
    if (!softwareIds.includes(action.payload.SoftwareId)) {
      softwareIds.push(action.payload.SoftwareId);
    }
    if (action.payload.Id != null && action.payload.SoftwareId != null) {
      //find and delete old entry to prevent duplicates if softwareId has changed
      const softwares = { ...state.softwares };
      const oldKey = Object.keys(softwares).find((k) => k.endsWith(String(action.payload.Id)));
      if (oldKey) {
        delete softwares[oldKey];
      }
      return {
        ...state,
        softwares: { ...softwares, [action.payload.SoftwareId + action.payload.Id]: { ...action.payload } },
        softwareIds,
      };
    } else {
      return;
    }
  });
});

export default SoftwareReducer;
